import {Async, Empty} from 'Core/models/Async';
import {createAction} from 'Shared/utilities/actionCreator';
import {
  A360AdjustmentDetails,
  A360EarnedStatement,
  A360EmployerSummary,
  A360AdvancedStatement,
  CommissionSummaryGrouped,
  PaymentReceipt,
  Producer,
} from '../components/A360Commissions/types';

export enum A360CommissionActionType {
  GET_AVAILABLE_PRODUCER_CODES = 'a360Commission/GET_AVAILABLE_PRODUCER_CODES',
  CHANGE_AVAILABLE_PRODUCER_CODES = 'a360Commission/CHANGE_AVAILABLE_PRODUCER_CODES',
  GET_AVAILABLE_EMPLOYER_NAMES = 'a360Commission/GET_AVAILABLE_EMPLOYER_NAMES',
  CHANGE_AVAILABLE_EMPLOYER_NAMES = 'a360Commission/CHANGE_AVAILABLE_EMPLOYER_NAMES',
  GET_AVAILABLE_STATEMENT_DATES = 'a360Commission/GET_AVAILABLE_STATEMENT_DATES',
  CHANGE_AVAILABLE_STATEMENT_DATES = 'a360Commission/CHANGE_AVAILABLE_STATEMENT_DATES',
  GET_EMPLOYER_SUMMARY = 'a360Commission/GET_EMPLOYER_SUMMARY',
  CHANGE_EMPLOYER_SUMMARY = 'a360Commission/CHANGE_EMPLOYER_SUMMARY',
  GET_EARNED_STATEMENTS = 'a360Commission/GET_EARNED_STATEMENTS',
  CHANGE_EARNED_STATEMENTS = 'a360Commission/CHANGE_EARNED_STATEMENTS',
  GET_ADVANCED_STATEMENTS = 'a360Commission/GET_ADVANCED_STATEMENTS',
  CHANGE_ADVANCED_STATEMENTS = 'a360Commission/CHANGE_ADVANCED_STATEMENTS',
  GET_COMMISSION_PAYABLE = 'a360Commission/GET_COMMISSION_PAYABLE',
  CHANGE_COMMISSION_PAYABLE = 'a360Commission/CHANGE_COMMISSION_PAYABLE',
  GET_PAYMENT_RECEIPTS = 'a360Commission/GET_PAYMENT_RECEIPTS',
  CHANGE_PAYMENT_RECEIPTS = 'a360Commission/CHANGE_PAYMENT_RECEIPTS',
  GET_COMMISSIONS_SUMMARY = 'a360Commission/GET_COMMISSIONS_SUMMARY',
  CHANGE_COMMISSIONS_SUMMARY = 'a360Commission/CHANGE_COMMISSIONS_SUMMARY',
  GET_ADJUSTMENT_DETAILS = 'a360Commission/GET_ADJUSTMENT_DETAILS',
  CHANGE_ADJUSTMENT_DETAILS = 'a360Commission/CHANGE_ADJUSTMENT_DETAILS',
  IS_INITIAL_FILTER_LOADING = 'a360Commission/IS_INITIAL_FILTER_LOADING',
  UPDATE_SELECTED_PRODUCER_CODES = 'a360Commission/UPDATE_SELECTED_PRODUCER_CODES',
  UPDATE_SELECTED_STATEMENT_DATES = 'a360Commission/UPDATE_SELECTED_STATEMENT_DATES',
  UPDATE_SELECTED_EMPLOYER_NAMES = 'a360Commission/UPDATE_SELECTED_EMPLOYER_NAMES',
  CLEAR_STATEMENT_DATE_AND_EMPLOYER_NAME_FILTERS = 'a360Commission/CLEAR_STATEMENT_DATE_AND_EMPLOYER_NAME_FILTERS',
  CLEAR_ALL_FILTERS = 'a360Commission/CLEAR_ALL_FILTERS',
  CLEAR_EMPLOYER_NAMES_FILTER = 'a360Commission/CLEAR_EMPLOYER_NAMES_FILTER',
  CLEAR_A360_COMMISSIONS_STATE = 'a360Commission/CLEAR_A360_COMMISSIONS_STATE',
}

export type CommissionReportGridData<T> = {
  results: T[];
  totalCount: number;
};

export type A360CommissionState = {
  // filters
  availableProducerCodes: Async<Producer[]>;
  availableEmployerNames: Async<string[]>;
  availableStatementDates: Async<string[]>;

  filter: {
    selectedProducerCodes: string[];
    selectedEmployerNames: string[];
    selectedStatementDates: string[];
  };

  isInitialFilterLoading: boolean;

  commissionPayable: Async<number>;
  commissionSummary: Async<CommissionSummaryGrouped>;
  paymentReceipts: Async<PaymentReceipt[]>;
  employerSummary: Async<CommissionReportGridData<A360EmployerSummary>>;
  earnedStatements: Async<CommissionReportGridData<A360EarnedStatement>>;
  advancedStatements: Async<CommissionReportGridData<A360AdvancedStatement>>;
  adjustmentDetails: Async<A360AdjustmentDetails[]>;
};

export const DEFAULT_STATE: A360CommissionState = {
  availableProducerCodes: new Empty(),
  availableEmployerNames: new Empty(),
  availableStatementDates: new Empty(),

  filter: {
    selectedProducerCodes: [],
    selectedEmployerNames: [],
    selectedStatementDates: [],
  },

  isInitialFilterLoading: false,

  commissionPayable: new Empty(),
  employerSummary: new Empty(),
  earnedStatements: new Empty(),
  advancedStatements: new Empty(),
  paymentReceipts: new Empty(),
  commissionSummary: new Empty(),
  adjustmentDetails: new Empty(),
};

export const DEFAULT_GRID_PAGE_SIZE = 15;
export const DEFAULT_SELECTED_PAGE = 0;
export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 15];

export const DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY = 'statementDate';
export const DEFAULT_SORT_FIELD_EARNED_STATEMENTS = DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY;
export const DEFAULT_SORT_FIELD_ADVANCED_STATEMENTS = DEFAULT_SORT_FIELD_EMPLOYER_SUMMARY;

export const changeIsInitialFilterLoading = (isLoading: boolean) =>
  createAction(A360CommissionActionType.IS_INITIAL_FILTER_LOADING, {isLoading});

export const getAvailableProducerCodes = (isNewYorkSelected: boolean) => createAction(A360CommissionActionType.GET_AVAILABLE_PRODUCER_CODES, {isNewYorkSelected});

export const updateSelectedProducerCodes = (producerCodes: string[]) =>
  createAction(A360CommissionActionType.UPDATE_SELECTED_PRODUCER_CODES, {producerCodes});

export const changeAvailableProducerCodes = (producerCodes: Async<Producer[]>) =>
  createAction(A360CommissionActionType.CHANGE_AVAILABLE_PRODUCER_CODES, {producerCodes});

export const getAvailableEmployerNames = () => createAction(A360CommissionActionType.GET_AVAILABLE_EMPLOYER_NAMES);

export const changeAvailableEmployerNames = (employerNames: Async<string[]>) =>
  createAction(A360CommissionActionType.CHANGE_AVAILABLE_EMPLOYER_NAMES, {employerNames});

export const getAvailableStatementDates = () => createAction(A360CommissionActionType.GET_AVAILABLE_STATEMENT_DATES);
export const changeAvailableStatementDates = (statementDates: Async<string[]>) =>
  createAction(A360CommissionActionType.CHANGE_AVAILABLE_STATEMENT_DATES, {statementDates});

export const updateSelectedStatementDates = (statementDates: string[]) =>
  createAction(A360CommissionActionType.UPDATE_SELECTED_STATEMENT_DATES, {statementDates});

export const updateSelectedEmployerNames = (employerNames: string[]) =>
  createAction(A360CommissionActionType.UPDATE_SELECTED_EMPLOYER_NAMES, {employerNames});

export const clearStatementDateAndEmployerNameFilter = () =>
  createAction(A360CommissionActionType.CLEAR_STATEMENT_DATE_AND_EMPLOYER_NAME_FILTERS);

export const clearAllFilters = () => createAction(A360CommissionActionType.CLEAR_ALL_FILTERS);

export const clearEmployerNamesFilter = () => createAction(A360CommissionActionType.CLEAR_EMPLOYER_NAMES_FILTER);

export const getEmployerSummary = (page: number, pageSize: number, sortFields: string[]) =>
  createAction(A360CommissionActionType.GET_EMPLOYER_SUMMARY, {
    page,
    pageSize,
    sortFields,
  });

export const changeEmployerSummary = (employerSummary: Async<CommissionReportGridData<A360EmployerSummary>>) =>
  createAction(A360CommissionActionType.CHANGE_EMPLOYER_SUMMARY, {employerSummary});

export const getEarnedStatements = (page: number, pageSize: number, sortFields: string[]) =>
  createAction(A360CommissionActionType.GET_EARNED_STATEMENTS, {
    page,
    pageSize,
    sortFields,
  });

export const changeEarnedStatements = (earnedStatements: Async<CommissionReportGridData<A360EarnedStatement>>) =>
  createAction(A360CommissionActionType.CHANGE_EARNED_STATEMENTS, {earnedStatements});

export const getAdvancedStatements = (page: number, pageSize: number, sortFields: string[]) =>
  createAction(A360CommissionActionType.GET_ADVANCED_STATEMENTS, {
    page,
    pageSize,
    sortFields,
  });

export const changeAdvancedStatements = (advancedStatements: Async<CommissionReportGridData<A360AdvancedStatement>>) =>
  createAction(A360CommissionActionType.CHANGE_ADVANCED_STATEMENTS, {advancedStatements});

export const getCommissionPayable = () => createAction(A360CommissionActionType.GET_COMMISSION_PAYABLE);

export const changeCommissionPayable = (commissionPayable: Async<number>) =>
  createAction(A360CommissionActionType.CHANGE_COMMISSION_PAYABLE, {commissionPayable});

export const getPaymentReceipts = () => createAction(A360CommissionActionType.GET_PAYMENT_RECEIPTS);

export const changePaymentReceipts = (paymentReceipts: Async<PaymentReceipt[]>) =>
  createAction(A360CommissionActionType.CHANGE_PAYMENT_RECEIPTS, {paymentReceipts});

export const getCommissionSummary = () => createAction(A360CommissionActionType.GET_COMMISSIONS_SUMMARY);

export const changeCommissionSummary = (commissionSummary: Async<CommissionSummaryGrouped>) =>
  createAction(A360CommissionActionType.CHANGE_COMMISSIONS_SUMMARY, {commissionSummary});

export const getAdjustmentDetails = () => createAction(A360CommissionActionType.GET_ADJUSTMENT_DETAILS);

export const clearA360CommissionsState = () => createAction(A360CommissionActionType.CLEAR_A360_COMMISSIONS_STATE);

export const changeAdjustmentDetails = (adjustmentDetails: Async<A360AdjustmentDetails[]>) =>
  createAction(A360CommissionActionType.CHANGE_ADJUSTMENT_DETAILS, {adjustmentDetails});

type A360CommissionAction =
  | ReturnType<typeof changeAvailableProducerCodes>
  | ReturnType<typeof changeEmployerSummary>
  | ReturnType<typeof changeEarnedStatements>
  | ReturnType<typeof changeAdvancedStatements>
  | ReturnType<typeof changeCommissionPayable>
  | ReturnType<typeof changePaymentReceipts>
  | ReturnType<typeof changeCommissionSummary>
  | ReturnType<typeof changeIsInitialFilterLoading>
  | ReturnType<typeof updateSelectedProducerCodes>
  | ReturnType<typeof changeAvailableStatementDates>
  | ReturnType<typeof updateSelectedStatementDates>
  | ReturnType<typeof changeAvailableEmployerNames>
  | ReturnType<typeof updateSelectedEmployerNames>
  | ReturnType<typeof clearStatementDateAndEmployerNameFilter>
  | ReturnType<typeof clearAllFilters>
  | ReturnType<typeof clearEmployerNamesFilter>
  | ReturnType<typeof clearA360CommissionsState>
  | ReturnType<typeof changeAdjustmentDetails>;

export default (state: A360CommissionState = DEFAULT_STATE, action: A360CommissionAction): A360CommissionState => {
  switch (action.type) {
    case A360CommissionActionType.CHANGE_AVAILABLE_PRODUCER_CODES:
      return {
        ...state,
        availableProducerCodes: action.payload.producerCodes,
      };
    case A360CommissionActionType.CHANGE_EMPLOYER_SUMMARY:
      return {
        ...state,
        employerSummary: action.payload.employerSummary,
      };
    case A360CommissionActionType.CHANGE_EARNED_STATEMENTS:
      return {
        ...state,
        earnedStatements: action.payload.earnedStatements,
      };
    case A360CommissionActionType.CHANGE_ADVANCED_STATEMENTS:
      return {
        ...state,
        advancedStatements: action.payload.advancedStatements,
      };
    case A360CommissionActionType.CHANGE_COMMISSION_PAYABLE:
      return {
        ...state,
        commissionPayable: action.payload.commissionPayable,
      };
    case A360CommissionActionType.CHANGE_PAYMENT_RECEIPTS:
      return {
        ...state,
        paymentReceipts: action.payload.paymentReceipts,
      };
    case A360CommissionActionType.CHANGE_COMMISSIONS_SUMMARY:
      return {
        ...state,
        commissionSummary: action.payload.commissionSummary,
      };
    case A360CommissionActionType.CHANGE_ADJUSTMENT_DETAILS:
      return {
        ...state,
        adjustmentDetails: action.payload.adjustmentDetails,
      };
    case A360CommissionActionType.UPDATE_SELECTED_PRODUCER_CODES:
      return {
        ...state,
        filter: {...state.filter, selectedProducerCodes: action.payload.producerCodes},
      };
    case A360CommissionActionType.CHANGE_AVAILABLE_STATEMENT_DATES:
      return {
        ...state,
        availableStatementDates: action.payload.statementDates,
      };
    case A360CommissionActionType.UPDATE_SELECTED_STATEMENT_DATES:
      return {
        ...state,
        filter: {...state.filter, selectedStatementDates: action.payload.statementDates},
      };
    case A360CommissionActionType.CHANGE_AVAILABLE_EMPLOYER_NAMES:
      return {
        ...state,
        availableEmployerNames: action.payload.employerNames,
      };
    case A360CommissionActionType.UPDATE_SELECTED_EMPLOYER_NAMES:
      return {
        ...state,
        filter: {...state.filter, selectedEmployerNames: action.payload.employerNames},
      };
    case A360CommissionActionType.CLEAR_STATEMENT_DATE_AND_EMPLOYER_NAME_FILTERS:
      return {
        ...state,
        availableEmployerNames: new Empty(),
        filter: {
          ...state.filter,
          selectedStatementDates: [],
          selectedEmployerNames: [],
        },
      };
    case A360CommissionActionType.CLEAR_ALL_FILTERS:
      return {
        ...state,
        availableStatementDates: new Empty(),
        availableEmployerNames: new Empty(),
        filter: {
          ...state.filter,
          selectedProducerCodes: [],
          selectedStatementDates: [],
          selectedEmployerNames: [],
        },
      };
    case A360CommissionActionType.CLEAR_EMPLOYER_NAMES_FILTER:
      return {
        ...state,
        filter: {
          ...state.filter,
          selectedEmployerNames: [],
        },
      };
    case A360CommissionActionType.IS_INITIAL_FILTER_LOADING:
      return {
        ...state,
        isInitialFilterLoading: action.payload.isLoading,
      };

    case A360CommissionActionType.CLEAR_A360_COMMISSIONS_STATE:
      return DEFAULT_STATE;

    default:
      return state;
  }
};
